import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  LinearProgress,
  Box,
  Select,
  MenuItem,
  Tooltip
} from "@mui/material";
import { PlayArrow, Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import Lottie from "react-lottie-player";

import { getDatabase, ref, onValue, push, remove, update, set } from "firebase/database";
import app from "../../../firebaseConfig";

// Animaciones
import greeRobot from "./assets/lottie/greeRobot.json";
import lottiePrinter from "./assets/lottie/lottiePrinter.json";
import torusLoading from "./assets/lottie/torusLoading.json";

const db = getDatabase(app);
const DB_PATH = "nature/Awoo/Control/ControlGranja";
const SETTINGS_COLUMNS_PATH = "settings/columns"; // settings/columns/{deviceId}
const SETTINGS_GLOBAL_PATH = "settings/global";   // settings/global/fontSize

// device id key in localStorage
const DEVICE_ID_KEY = "gestor_device_id_v1";
// local settings cache key (per device)
const LOCAL_SETTINGS_PREFIX = "gestor_settings_";

function generateDeviceId() {
  if (typeof window === "undefined") return "dev-server";
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) {}
  return `dev-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export default function GestorDisenos() {
  const [open, setOpen] = useState(false);
  const [disenos, setDisenos] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [nuevoDiseno, setNuevoDiseno] = useState({
    nombre: "",
    tiempo: "",
    capas: "",
    pausas: []
  });

  // Settings
  const [deviceId, setDeviceId] = useState(null);
  const [columns, setColumns] = useState(3);        // default 3 columnas
  const [fontSize, setFontSize] = useState(14);     // default 14px
  const [minHeight, setMinHeight] = useState(380);  // default min height px

  // constants / clamps
  const MIN_MINHEIGHT = 200;
  const MAX_MINHEIGHT = 1000;
  const MIN_COLUMNS = 1;
  const MAX_COLUMNS = 7;
  const MIN_FONTSIZE = 10;
  const MAX_FONTSIZE = 32;

  // Formato hh:mm:ss
  const formatTime = (seconds) => {
    if (seconds == null) return "0s";
    seconds = Math.max(0, Math.floor(seconds));
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s}s`;
  };

  // Helper: actualizar parcial en Firebase para items
  const updateItemInDB = async (id, partial) => {
    try {
      await update(ref(db, `${DB_PATH}/${id}`), partial);
    } catch (err) {
      console.error("Error actualizando item en DB:", err);
    }
  };

  // Helper persist settings for this device (columns + minHeight)
  const persistDeviceSettings = async (devId, cols, height) => {
    const payload = {
      columns: cols,
      minHeight: height,
      lastUpdated: Date.now()
    };
    try {
      await set(ref(db, `${SETTINGS_COLUMNS_PATH}/${devId}`), payload);
    } catch (err) {
      console.error("Error guardando settings en DB:", err);
    }
    // save local copy for instant offline read
    try {
      localStorage.setItem(LOCAL_SETTINGS_PREFIX + devId, JSON.stringify(payload));
    } catch (e) {
      // ignore localStorage errors
    }
  };

  // Helper: update global font setting
  const persistGlobalFontSize = async (value) => {
    try {
      await set(ref(db, `${SETTINGS_GLOBAL_PATH}/fontSize`), value);
    } catch (err) {
      console.error("Error guardando fontSize global:", err);
    }
  };

  // --- Device id init & listen settings ---
  useEffect(() => {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = generateDeviceId();
      try {
        localStorage.setItem(DEVICE_ID_KEY, id);
      } catch (e) {
        console.warn("No se pudo guardar deviceId en localStorage:", e);
      }
    }
    setDeviceId(id);

    // try load cached local settings first for instant UI
    try {
      const cached = localStorage.getItem(LOCAL_SETTINGS_PREFIX + id);
      if (cached) {
        const obj = JSON.parse(cached);
        if (obj && typeof obj.columns === "number") setColumns(Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, obj.columns)));
        if (obj && typeof obj.minHeight === "number") setMinHeight(Math.max(MIN_MINHEIGHT, Math.min(MAX_MINHEIGHT, obj.minHeight)));
      }
    } catch (e) {
      // ignore parse errors
    }

    // listen for this device's columns + minHeight setting (now 1..7)
    const columnsRef = ref(db, `${SETTINGS_COLUMNS_PATH}/${id}`);
    const unColumns = onValue(columnsRef, (snap) => {
      const v = snap.val();
      if (!v) return;
      if (typeof v.columns === "number") setColumns(Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, v.columns)));
      if (typeof v.minHeight === "number") setMinHeight(Math.max(MIN_MINHEIGHT, Math.min(MAX_MINHEIGHT, v.minHeight)));
    });

    // listen global fontSize setting
    const fontRef = ref(db, `${SETTINGS_GLOBAL_PATH}/fontSize`);
    const unFont = onValue(fontRef, (snap) => {
      const v = snap.val();
      if (typeof v === "number" && !Number.isNaN(v)) {
        setFontSize(Math.max(MIN_FONTSIZE, Math.min(MAX_FONTSIZE, v)));
      }
    });

    return () => {
      unColumns();
      unFont();
    };
  }, []);

  // --- Escuchar Firebase en tiempo real para items ---
  useEffect(() => {
    const itemsRef = ref(db, DB_PATH);
    const unsub = onValue(itemsRef, (snapshot) => {
      const val = snapshot.val();
      if (!val) {
        setDisenos([]);
        return;
      }

      const now = Date.now();

      const loaded = Object.keys(val).map((key) => {
        const raw = val[key] || {};

        let pausas = [];
        if (Array.isArray(raw.pausas)) pausas = raw.pausas.map((p) => Number(p));
        else if (raw.pausas && typeof raw.pausas === "object")
          pausas = Object.values(raw.pausas).map((p) => Number(p));
        else pausas = [];

        const tiempoTotal = Number(raw.tiempoTotal) || 0;
        const tiempoRestanteStored = Number(raw.tiempoRestante ?? tiempoTotal) || 0;
        const capas = Math.max(1, Number(raw.capas) || 1);
        const tiempoPorCapa = Number(raw.tiempoPorCapa) || (capas ? tiempoTotal / capas : 0);
        const corriendoStored = !!raw.corriendo;
        const lastUpdated = Number(raw.lastUpdated) || now;
        let tiempoRestanteAdjusted = tiempoRestanteStored;
        let corriendo = corriendoStored;
        let estado = raw.estado ?? (corriendo ? "Imprimiendo" : "Esperando");
        let blink = !!raw.blink;

        if (corriendoStored) {
          const elapsed = Math.max(0, Math.floor((now - lastUpdated) / 1000));
          tiempoRestanteAdjusted = Math.max(0, tiempoRestanteStored - elapsed);

          if (tiempoRestanteAdjusted <= 0) {
            estado = "Terminado";
            corriendo = false;
            blink = true;
            tiempoRestanteAdjusted = 0;
          } else {
            estado = "Imprimiendo";
          }
        } else {
          if (tiempoRestanteAdjusted <= 0) {
            estado = "Terminado";
            blink = true;
          }
        }

        const capaActual = Math.min(
          capas,
          tiempoPorCapa > 0 ? Math.floor((tiempoTotal - tiempoRestanteAdjusted) / tiempoPorCapa) + 1 : 1
        );

        return {
          id: key,
          nombre: raw.nombre ?? "Sin nombre",
          tiempoTotal,
          tiempoRestante: tiempoRestanteAdjusted,
          tiempoRestanteStored,
          capas,
          tiempoPorCapa,
          pausas,
          corriendo,
          estado,
          lastUpdated,
          capaActual,
          blink
        };
      });

      loaded.sort((a, b) => (b.lastUpdated || 0) - (a.lastUpdated || 0));
      setDisenos(loaded);
    });

    return () => unsub();
  }, []);

  // Abrir/cerrar dialog
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNuevoDiseno({ nombre: "", tiempo: "", capas: "", pausas: [] });
  };

  // Añadir pausa (local al crear)
  const handleAddPause = () => {
    const capa = prompt("¿En qué capa se pausa?");
    if (capa) {
      setNuevoDiseno((prev) => ({
        ...prev,
        pausas: [...prev.pausas, parseInt(capa, 10)]
      }));
    }
  };

  // Guardar diseño -> push a Firebase
  const handleAddDiseno = async () => {
    const tiempoMinutos = Number(nuevoDiseno.tiempo) || 0;
    const tiempoEnSegundos = Math.floor(tiempoMinutos * 60);
    const capasTotales = Math.max(1, Number(nuevoDiseno.capas) || 1);
    const tiempoPorCapa = capasTotales ? tiempoEnSegundos / capasTotales : tiempoEnSegundos;

    const newItem = {
      nombre: nuevoDiseno.nombre,
      tiempoTotal: tiempoEnSegundos,
      tiempoRestante: tiempoEnSegundos,
      capas: capasTotales,
      capaActual: 1,
      tiempoPorCapa,
      corriendo: false,
      estado: "Esperando",
      blink: false,
      pausas: nuevoDiseno.pausas.map((p) => Number(p)),
      lastUpdated: Date.now()
    };

    try {
      const itemsRef = ref(db, DB_PATH);
      await push(itemsRef, newItem);
      handleClose();
    } catch (err) {
      console.error("Error creando diseño en Firebase:", err);
    }
  };

  // Control cronómetros local (cada 1s) — no escribe cada segundo en DB,
  // sólo escribe cuando hay un evento: pausa alcanzada o terminado.
  useEffect(() => {
    const interval = setInterval(() => {
      setDisenos((prev) => {
        const updatesToPersist = [];
        const now = Date.now();

        const next = prev.map((d) => {
          if (!d.corriendo || d.tiempoRestante <= 0) {
            if (d.tiempoRestante <= 0 && d.estado !== "Terminado") {
              updatesToPersist.push({
                id: d.id,
                partial: { corriendo: false, estado: "Terminado", tiempoRestante: 0, blink: true, lastUpdated: now }
              });
              return { ...d, corriendo: false, estado: "Terminado", tiempoRestante: 0, blink: true, lastUpdated: now };
            }
            return d;
          }

          const nuevoTiempo = Math.max(0, d.tiempoRestante - 1);
          const tiempoPorCapa = d.tiempoPorCapa || (d.capas ? d.tiempoTotal / d.capas : d.tiempoTotal);
          const capaActual = Math.min(
            d.capas,
            tiempoPorCapa > 0 ? Math.floor((d.tiempoTotal - nuevoTiempo) / tiempoPorCapa) + 1 : 1
          );

          if (d.pausas && d.pausas.includes(capaActual) && capaActual !== d.capaActual) {
            updatesToPersist.push({
              id: d.id,
              partial: {
                tiempoRestante: nuevoTiempo,
                corriendo: false,
                estado: "Pausa",
                capaActual,
                lastUpdated: now
              }
            });
            return { ...d, tiempoRestante: nuevoTiempo, corriendo: false, estado: "Pausa", capaActual, lastUpdated: now };
          }

          if (nuevoTiempo <= 0) {
            updatesToPersist.push({
              id: d.id,
              partial: { tiempoRestante: 0, corriendo: false, estado: "Terminado", blink: true, capaActual, lastUpdated: now }
            });
            return { ...d, tiempoRestante: 0, corriendo: false, estado: "Terminado", blink: true, capaActual, lastUpdated: now };
          }

          return { ...d, tiempoRestante: nuevoTiempo, capaActual, estado: "Imprimiendo" };
        });

        if (updatesToPersist.length > 0) {
          updatesToPersist.forEach(({ id, partial }) => {
            updateItemInDB(id, partial);
          });
        }

        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Blink en "Terminado"
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setDisenos((prev) =>
        prev.map((d) =>
          d.estado === "Terminado" ? { ...d, blink: !d.blink } : d
        )
      );
    }, 800);
    return () => clearInterval(blinkInterval);
  }, []);

  // Reproducir (play) -> actualizar en Firebase (corriendo true)
  const handlePlay = async (id) => {
    try {
      await update(ref(db, `${DB_PATH}/${id}`), {
        corriendo: true,
        estado: "Imprimiendo",
        lastUpdated: Date.now()
      });
    } catch (err) {
      console.error("Error al poner a correr en DB:", err);
    }
  };

  // Eliminar (confirma primero)
  const handleDelete = (id) => setConfirmDelete(id);

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    try {
      await remove(ref(db, `${DB_PATH}/${confirmDelete}`));
      setConfirmDelete(null);
    } catch (err) {
      console.error("Error eliminando nodo en DB:", err);
    }
  };

  // Cambiar número de columnas (solo local + persistir setting por dispositivo)
  const handleColumnsChange = async (value) => {
    const cols = Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, Number(value)));
    setColumns(cols);
    if (!deviceId) return;
    // persist both columns and minHeight for this device
    await persistDeviceSettings(deviceId, cols, minHeight);
  };

  // Cambiar font size global (persistir global)
  const handleChangeFont = async (delta) => {
    const next = Math.max(MIN_FONTSIZE, Math.min(MAX_FONTSIZE, Math.round(fontSize + delta)));
    setFontSize(next);
    await persistGlobalFontSize(next);
  };

  // Cambiar minHeight (por dispositivo)
  const handleChangeMinHeight = async (delta) => {
    const next = Math.max(MIN_MINHEIGHT, Math.min(MAX_MINHEIGHT, Math.round(minHeight + delta)));
    setMinHeight(next);
    if (!deviceId) return;
    await persistDeviceSettings(deviceId, columns, next);
  };

  // Colores estado
  const getEstadoStyle = (estado, blink) => {
    switch (estado) {
      case "Esperando":
        return { background: "#007bff", color: "white" };
      case "Imprimiendo":
        return { background: "green", color: "white" };
      case "Pausa":
        return { background: "orange", color: "white" };
      case "Terminado":
        return {
          background: blink ? "white" : "yellow",
          color: blink ? "green" : "black",
          border: "2px solid green"
        };
      default:
        return {};
    }
  };

  return (
    <div style={{ padding: 20, fontSize: `${fontSize}px`, color: "white" }}>
      {/* Top controls: Robot, Crear, Selector Columnas, Font +/-, MinHeight +/- */}
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <Lottie animationData={greeRobot} play loop style={{ width: 60, height: 60 }} />
        <Button variant="contained" onClick={handleOpen}>Crear Diseño</Button>

        {/* Selector columnas (1-7) */}
        <Box display="flex" alignItems="center" gap={1} sx={{ background: "rgba(255,255,255,0.06)", px:1, py:0.5, borderRadius:1 }}>
          <Typography variant="body2" sx={{ color: "white" }}>Columnas:</Typography>
          <Select
            value={columns}
            size="small"
            onChange={(e) => handleColumnsChange(e.target.value)}
            sx={{
              minWidth: 80,
              color: "white",
              borderColor: "white",
              "& .MuiSvgIcon-root": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" }
            }}
          >
            {[1,2,3,4,5,6,7].map((n) => (<MenuItem key={n} value={n}>{n}</MenuItem>))}
          </Select>
        </Box>

        {/* Font size controls */}
        <Box display="flex" alignItems="center" gap={1} sx={{ background: "rgba(255,255,255,0.06)", px:1, py:0.5, borderRadius:1 }}>
          <Typography variant="body2" sx={{ color: "white" }}>Fuente:</Typography>
          <Tooltip title="Disminuir fuente">
            <IconButton size="small" onClick={() => handleChangeFont(-1)} sx={{ color: "white", border: "1px solid white" }}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" sx={{ color: "white" }}>{fontSize}px</Typography>
          <Tooltip title="Aumentar fuente">
            <IconButton size="small" onClick={() => handleChangeFont(+1)} sx={{ color: "white", border: "1px solid white" }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* MinHeight controls (per device) */}
        <Box display="flex" alignItems="center" gap={1} sx={{ background: "rgba(255,255,255,0.06)", px:1, py:0.5, borderRadius:1 }}>
          <Typography variant="body2" sx={{ color: "white" }}>Card H:</Typography>
          <Tooltip title="Disminuir altura mínima">
            <IconButton size="small" onClick={() => handleChangeMinHeight(-20)} sx={{ color: "white", border: "1px solid white" }}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" sx={{ color: "white" }}>{minHeight}px</Typography>
          <Tooltip title="Aumentar altura mínima">
            <IconButton size="small" onClick={() => handleChangeMinHeight(+20)} sx={{ color: "white", border: "1px solid white" }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="caption" sx={{ ml: 2, opacity: 0.7 }}>
          Device: {deviceId ? deviceId.slice(0, 8) : "—"}
        </Typography>
      </Box>

      {/* Grid */}
      <div
        style={{
          marginTop: 20,
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, minmax(220px, 1fr))`,
          gap: "15px",
          maxHeight: "70vh",
          overflowY: "auto"
        }}
      >
        {disenos.map((d) => {
          const rawPct = d.tiempoTotal > 0 ? ((d.tiempoTotal - d.tiempoRestante) / d.tiempoTotal) * 100 : 0;
          const porcentaje = Math.max(0, Math.min(100, rawPct));

          const fontScaleStyle = { fontSize: `${fontSize}px` };

          return (
            <Card
              key={d.id}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: `${minHeight}px`, // <-- usa minHeight dinámico
                boxShadow: "0 0 15px rgba(0, 123, 255, 0.6)",
                border: "2px solid rgba(0,123,255,0.5)",
                borderRadius: "15px",
                backgroundColor: "rgba(30,30,30,0.95)",
                transition: "transform 0.2s, box-shadow 0.2s",
                overflow: "hidden",
                "&:hover": { boxShadow: "0 0 20px rgba(0, 200, 255, 0.9)" },
              }}
            >
              {/* Botón eliminar */}
              <Box
                sx={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  background: "red",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontWeight: "bold",
                  color: "white",
                  fontSize: "16px",
                  boxShadow: "0 0 8px rgba(0,0,0,0.3)",
                }}
                onClick={() => handleDelete(d.id)}
              >
                X
              </Box>

              <CardContent>
                {/* Encabezado: Animación + Nombre + Estado (abajo del nombre) */}
                <Box display="flex" flexDirection="row" alignItems="center" gap={2} mb={1} justifyContent="center">
                  <Lottie animationData={lottiePrinter} play loop style={{ width: 90, height: 90 }} />

                  {/* Nombre del diseño */}
                  <Box>
                    <Typography variant="h6" sx={{ ...fontScaleStyle, color: "white", fontWeight: "bold" }}>
                      {d.nombre}
                    </Typography>

                    {/* Estado debajo del nombre */}
                    <Box sx={{ mt: 0.5, px: 2, py: 0.5, borderRadius: "12px", fontWeight: "bold", textAlign: "center", ...getEstadoStyle(d.estado, d.blink), ...fontScaleStyle }}>
                      {d.estado}
                    </Box>
                  </Box>
                </Box>

                {/* Datos secundarios */}
                <Typography variant="body2" sx={{ ...fontScaleStyle, color: "white" }}>
                  Capas: {d.capas} | Tiempo total: {formatTime(d.tiempoTotal)}
                </Typography>

                {d.pausas && d.pausas.length > 0 && (
                  <Typography variant="body2" sx={{ ...fontScaleStyle, color: "white" }}>
                    Pausas en capas: {d.pausas.join(", ")}
                  </Typography>
                )}

                {/* Tiempo restante y capa actual */}
                <Typography variant="h6" sx={{ ...fontScaleStyle, color: "#00eaff" }} align="center" mt={1}>
                  ⏱ {formatTime(d.tiempoRestante)} restantes
                </Typography>
                <Typography variant="body1" sx={{ ...fontScaleStyle, color: "#80dfff" }} align="center">
                  🛠 Capa actual: {d.capaActual} / {d.capas}
                </Typography>

                {/* Barra de progreso */}
                <Box sx={{ width: "100%", mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress variant="determinate" value={porcentaje} sx={{ height: 12, borderRadius: 6, backgroundColor: "#444", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg, #007bff, #33ccff)", boxShadow: "0 0 10px #33ccff" } }} />
                    <Typography variant="body2" align="center" sx={{ mt: 0.5, fontWeight: "bold", color: "#33ccff", ...fontScaleStyle }}>
                      {porcentaje.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>

              {/* Botón Play */}
              <CardActions sx={{ justifyContent: "center" }}>
                <IconButton onClick={() => handlePlay(d.id)} sx={{ color: "#33ccff" }}>
                  <PlayArrow />
                </IconButton>
              </CardActions>
            </Card>
          );
        })}
      </div>

      {/* Dialog creación */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Crear Nuevo Diseño</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Nombre del archivo" fullWidth value={nuevoDiseno.nombre} onChange={(e) => setNuevoDiseno({ ...nuevoDiseno, nombre: e.target.value })} />
          <TextField margin="dense" label="Tiempo (minutos)" type="number" fullWidth value={nuevoDiseno.tiempo} onChange={(e) => setNuevoDiseno({ ...nuevoDiseno, tiempo: e.target.value })} />
          <TextField margin="dense" label="Número de capas" type="number" fullWidth value={nuevoDiseno.capas} onChange={(e) => setNuevoDiseno({ ...nuevoDiseno, capas: e.target.value })} />
          <Button onClick={handleAddPause} style={{ marginTop: 10 }}>+ Añadir Pausa</Button>
          {nuevoDiseno.pausas.length > 0 && (<Typography variant="body2" style={{ marginTop: 10 }}>Pausas en capas: {nuevoDiseno.pausas.join(", ")}</Typography>)}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleAddDiseno} variant="contained" disabled={!nuevoDiseno.nombre || !nuevoDiseno.tiempo || !nuevoDiseno.capas}>Añadir</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>¿Eliminar diseño?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button color="error" onClick={confirmDeleteAction}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
